import { useState, useEffect, useMemo } from 'react';
import API from '../../../Utils/api';
import { useUser } from '../../../Context/useUser';
import Table from '../../../Components/Table/Table';
import Utils from '../../../Utils';
import Line from '../../../Components/Charts/Line/Line';
import styles from './RaceAnalysisPage.module.css';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import UpdateRace from '../CalendarPage/UpdateRace';
import { MdDeleteOutline } from 'react-icons/md';
import { FiEdit3 } from 'react-icons/fi';
import CreateRace from '../CalendarPage/CreateRace';
import Modal from '../../../Components/Modal/Modal';

export default function RaceAnalysisPage () {
    const [raceData, setRaceData] = useState([]);
    const [racesLoading, setRacesLoading] = useState(true);
    const [selectedRaces, selectRaces] = useState([]);

    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [raceToEdit, setRaceToEdit] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [raceToDelete, setRaceToDelete] = useState(null);

    const { user } = useUser();

    const { athleteId } = useParams();

    const visitedUser = useVisitingUser(athleteId);

    const currentUser = athleteId ? visitedUser : user;

    const fetchAndSetData = async currentUser => {
        try {
            if (!currentUser) return;

            setRacesLoading(true);
            const { data: races } = await API.getRacesByUserId(currentUser._id);
            console.log(races);
            setRaceData(races);
        } catch (error) {
            toast.error(`Failed to fetch races - please contact support`, {
                position: 'bottom-center'
            });
        } finally {
            setRacesLoading(false);
        }
    };

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    useEffect(() => {
        if (raceData.length > 0) {
            selectRaces([raceData[raceData.length - 1]]);
        }
    }, [raceData]);

    const durationDataset = selectedRaces.map((race, i) => {
        const { red, green, blue } = Utils.randomColorGenerator(i);
        return {
            label: new Date(race.raceDate).toLocaleDateString('en-GB'),
            data: (race.splits || []).map(split => split.duration),
            borderColor: `rgb(${red}, ${green}, ${blue})`,
            backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`
        };
    });

    const strokeIndexDataset = selectedRaces.map((race, i) => {
        const { red, green, blue } = Utils.randomColorGenerator(i);
        return {
            label: new Date(race.raceDate).toLocaleDateString('en-GB'),
            data: [...(race.splits || []).map(split => split.strokeIndex)],
            borderColor: `rgb(${blue}, ${green}, ${red})`,
            backgroundColor: `rgba(${blue}, ${green}, ${red}, 0.5)`
        };
    });

    const strokeRateDataset = selectedRaces.map((race, i) => {
        const { red, green, blue } = Utils.randomColorGenerator(i);
        return {
            label: new Date(race.raceDate).toLocaleDateString('en-GB'),
            data: [...(race.splits || []).map(split => split.strokeRate)],
            borderColor: `rgb(${blue}, ${red}, ${green})`,
            backgroundColor: `rgba(${blue}, ${red}, ${green}, 0.5)`
        };
    });

    const strokeLengthDataset = selectedRaces.map((race, i) => {
        const { red, green, blue } = Utils.randomColorGenerator(i);
        return {
            label: new Date(race.raceDate).toLocaleDateString('en-GB'),
            data: [...(race.splits || []).map(split => split.strokeLength)],
            borderColor: `rgb(${green}, ${red}, ${blue})`,
            backgroundColor: `rgba(${green}, ${red}, ${blue}, 0.5)`
        };
    });

    const circlesTimeDataset = selectedRaces.map((race, i) => {
        const { red, green, blue } = Utils.randomColorGenerator(i);
        return {
            label: new Date(race.raceDate).toLocaleDateString('en-GB'),
            data: [...(race.splits || []).map(split => split.T)],
            borderColor: `rgb(${red}, ${green}, ${blue})`,
            backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`
        };
    });

    const velocityDataset = selectedRaces.map((race, i) => {
        const { red, green, blue } = Utils.randomColorGenerator(i);
        return {
            label: new Date(race.raceDate).toLocaleDateString('en-GB'),
            data: [...(race.splits || []).map(split => split.velocity)],
            borderColor: `rgb(${red}, ${green}, ${blue})`,
            backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`
        };
    });

    const splitRangeLabels = useMemo(() => {
        if (
            !selectedRaces.length ||
            !selectedRaces[0].splits ||
            selectedRaces[0].splits.length === 0
        )
            return [];

        const firstSelectedRace = selectedRaces[0];
        const raceType = parseInt(firstSelectedRace.raceType);
        const numberOfSplits = firstSelectedRace.splits.length;

        let calculatedSplitLength = 0;
        if (!isNaN(raceType) && numberOfSplits > 0) {
            calculatedSplitLength = raceType / numberOfSplits;
        } else {
            calculatedSplitLength = 25;
        }

        let start = 0;
        return firstSelectedRace.splits.map((split, idx) => {
            const currentSplitVolume =
                split.volume && typeof split.volume === 'number' && split.volume > 0
                    ? split.volume
                    : calculatedSplitLength;
            let end = start + currentSplitVolume;
            // Clamp the last split's end to raceType
            if (idx === numberOfSplits - 1 && !isNaN(raceType)) {
                end = raceType;
            }
            const label = `${start}-${end}`;
            start = end;
            return label;
        });
    }, [selectedRaces]);

    // Calculate total time from splits (duration is in seconds)
    const calculateTotalTime = splits => {
        if (!splits || splits.length === 0) return null;
        const total = splits.reduce((sum, split) => {
            const duration = Number(split.duration) || 0;
            return sum + duration;
        }, 0);
        return total === 0 ? null : total;
    };

    // Format time in MM:SS.MS format (minutes:seconds.milliseconds)
    const formatTime = totalSeconds => {
        if (totalSeconds === null || totalSeconds === undefined || totalSeconds === 0) {
            return '-';
        }

        // Round to 3 decimal places for precision
        const roundedSeconds = Math.round(totalSeconds * 1000) / 1000;

        const minutes = Math.floor(roundedSeconds / 60);
        const seconds = Math.floor(roundedSeconds % 60);
        const milliseconds = Math.floor((roundedSeconds % 1) * 1000);

        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
    };

    const raceColumns = [
        {
            Header: 'Race Date',
            Cell: ({ row }) => <div>{new Date(row.original.raceDate).toLocaleDateString()}</div>
        },
        {
            id: 'raceType',
            Header: () => <div style={{ textAlign: 'center' }}>Race Type</div>,
            accessor: 'raceType',
            Cell: ({ value }) => <div style={{ textAlign: 'center' }}>{value}</div>
        },
        {
            id: 'raceStyle',
            Header: () => <div style={{ textAlign: 'center' }}>Style</div>,
            accessor: 'raceStyle',
            Cell: ({ value }) => <div style={{ textAlign: 'center' }}>{value}</div>
        },
        {
            id: 'officialTime',
            Header: () => <div style={{ textAlign: 'center' }}>Official Time</div>,
            Cell: ({ row }) => {
                const officialTime = row.original.duration;
                return <div style={{ textAlign: 'center' }}>{formatTime(officialTime)}</div>;
            }
        },
        {
            id: 'splitsTotal',
            Header: () => <div style={{ textAlign: 'center' }}>Splits Total</div>,
            Cell: ({ row }) => {
                const totalTime = calculateTotalTime(row.original.splits);
                return <div style={{ textAlign: 'center' }}>{formatTime(totalTime)}</div>;
            }
        },
        {
            id: 'location',
            Header: () => <div style={{ textAlign: 'center' }}>Location</div>,
            accessor: 'raceLocation',
            Cell: ({ value }) => <div style={{ textAlign: 'center' }}>{value}</div>
        },
        {
            id: 'lastUpdated',
            Header: () => <div style={{ textAlign: 'center' }}>Last Updated</div>,
            Cell: ({ row }) =>
                row.original.updatedAt && (
                    <div style={{ textAlign: 'center' }}>{new Date(row.original.updatedAt).toLocaleString()}</div>
                )
        },
        {
            Header: 'Actions',
            Cell: ({ row }) => (
                <div className={styles.actionsCell}>
                    <span
                        style={{ borderColor: 'var(--bs-warning)' }}
                        className={styles.iconButton}
                        role='button'
                        tabIndex={0}
                        onClick={e => {
                            e.stopPropagation();
                            handleEditRace(row.original);
                        }}
                    >
                        <FiEdit3 />
                    </span>
                    <span
                        style={{ borderColor: 'var(--bs-danger)' }}
                        className={styles.iconButton}
                        role='button'
                        tabIndex={0}
                        onClick={() => {
                            setRaceToDelete(row.original);
                            setShowDeleteModal(true);
                        }}
                    >
                        <MdDeleteOutline />
                    </span>
                </div>
            )
        }
    ];

    const handleRowClick = row => {
        selectRaces(prev => {
            const alreadySelected = prev.some(r => r._id === row._id);
            if (alreadySelected) {
                return prev.filter(r => r._id !== row._id);
            } else {
                return [...prev, row];
            }
        });
    };

    const onRaceDelete = async () => {
        try {
            await API.deleteRaceById(raceToDelete._id);
            toast.success('Race deleted!', {
                position: 'bottom-center'
            });
            setShowDeleteModal(false);
            setRaceToDelete(null);
            fetchAndSetData(currentUser);
        } catch (err) {
            toast.error('Race could not be deleted - please contact support.', {
                position: 'bottom-center'
            });
        }
    };

    const handleEditRace = race => {
        setRaceToEdit({
            ...race,
            raceDate: race.raceDate ? new Date(race.raceDate).toISOString().slice(0, 10) : ''
        });
        setShowUpdateModal(true);
    };

    return (
        <div className={styles.pageWrap}>
            <h4 className={styles.title + ' island'}> Race Analysis</h4>
            <div className={styles.dataTablesWrap + ' island'}>
                <div className={styles.tableTopBar}>
                    <div className={styles.tableInfoLeft}>
                        <b>Tip:</b> Click the <i className={styles.circle}></i> icon to select
                        multiple races. For accurate comparison, please select races with the same
                        distance.
                        <br />
                        <b>Note:</b> Races without splits will not appear in the charts below.
                    </div>
                    <div>
                        <button
                            className='btn btn-primary'
                            onClick={() => setShowCreateModal(true)}
                        >
                            Create Race
                        </button>
                    </div>{' '}
                </div>
                <Table
                    onRowClick={handleRowClick}
                    columns={raceColumns}
                    data={raceData}
                    isLoading={racesLoading}
                    rowsPerPage={10}
                    updatePageSize={false}
                    pageSkipping={false}
                    selected={selectedRaces}
                    showSelectionIndicator={true}
                />
            </div>
            <div className={styles.dataVisualizationWrap}>
                <div className={styles.graph + ' island'}>
                    <Line
                        title='Duration (sec)'
                        titleX={'Distance (m)'}
                        datasets={durationDataset}
                        labels={splitRangeLabels}
                        xAxisType='category'
                    />
                </div>
                <div className={styles.graph + ' island'}>
                    <Line
                        title='Stroke Index [SI] (m^2/cycles/sec)'
                        titleX={'Distance (m)'}
                        datasets={strokeIndexDataset}
                        labels={splitRangeLabels}
                        xAxisType='category'
                    />
                </div>
                <div className={styles.graph + ' island'}>
                    <Line
                        title='Stroke Rate [SR] (cycles/min)'
                        titleX={'Distance (m)'}
                        datasets={strokeRateDataset}
                        labels={splitRangeLabels}
                        xAxisType='category'
                    />
                </div>
                <div className={styles.graph + ' island'}>
                    <Line
                        title='Stroke Length [SL] (m/cycle)'
                        titleX={'Distance (m)'}
                        datasets={strokeLengthDataset}
                        labels={splitRangeLabels}
                        xAxisType='category'
                    />
                </div>
                <div className={styles.graph + ' island'}>
                    <Line
                        title='3 Stroke Cycle Time [T] (sec)'
                        titleX={'Distance (m)'}
                        datasets={circlesTimeDataset}
                        labels={splitRangeLabels}
                        xAxisType='category'
                    />
                </div>
                <div className={styles.graph + ' island'}>
                    <Line
                        title='Velocity (m/sec)'
                        titleX={'Distance (m)'}
                        datasets={velocityDataset}
                        labels={splitRangeLabels}
                        xAxisType='category'
                    />
                </div>
            </div>
            {showUpdateModal && raceToEdit && (
                <UpdateRace
                    data={raceToEdit}
                    closeCB={() => setShowUpdateModal(false)}
                    onSuccess={() => {
                        setShowUpdateModal(false);
                        fetchAndSetData(currentUser);
                        toast.success(`Race updated successfully`, {
                            position: 'bottom-center'
                        });
                    }}
                    onFailure={() => {
                        setShowUpdateModal(false);
                        toast.error(`Error updating race - please contact support`, {
                            position: 'bottom-center'
                        });
                    }}
                />
            )}
            {showCreateModal && (
                <CreateRace
                    user={currentUser}
                    data={{ start: new Date() }}
                    closeCB={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        fetchAndSetData(currentUser);
                        toast.success(`Race created successfully`, {
                            position: 'bottom-center'
                        });
                    }}
                    onFailure={() => {
                        setShowCreateModal(false);
                        toast.error(`Error creating race - please contact support`, {
                            position: 'bottom-center'
                        });
                    }}
                />
            )}
            {showDeleteModal && (
                <Modal
                    title='Delete Race'
                    closeCB={() => {
                        setRaceToDelete(null);
                        setShowDeleteModal(false);
                    }}
                >
                    <div className={styles.modalContent}>
                        <p>Are you sure you want to delete this race?</p>
                        <button className={'btn btn-danger'} onClick={onRaceDelete}>
                            Confirm deletion
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
